import React, { useEffect } from "react";
import HomeSection from "../home/HomeSection";
import { useGetNbaSimilarSeasonsQuery } from "../../store/api/nbaApi";
import { useGetMlbSimilarSeasonsQuery } from "../../store/api/mlbApi";
import { useGetNflSimilarSeasonsQuery } from "../../store/api/nflApi";
import { useAnalytics } from "../../hooks/useAnalytics";
import { resolveDevSimilarityModel } from "./similarSeasonsDevConfig";
import { SIMILAR_SEASONS_ANCHOR } from "./similarSeasonsFormat";
import { MLB_SIMILAR_SEASON_DISPLAY } from "./similarSeasonsMlbDisplay";
import { NBA_SIMILAR_SEASON_DISPLAY } from "./similarSeasonsNbaDisplay";
import { NFL_SIMILAR_SEASON_DISPLAY } from "./similarSeasonsNflDisplay";
import { toSimilarSeasonItems } from "./similarSeasonsPresentation";
import { similarSeasonsGuideCopy } from "./similarSeasonsGuideCopy";
import SeasonChipRail from "./SeasonChipRail";
import SimilarSeasonsGuide from "./SimilarSeasonsGuide";
import SimilarSeasonsList from "./SimilarSeasonsList";

const SimilarSeasons = ({
  league,
  playerId,
  season,
  seasons = [],
  role,
  onSeasonChange,
}) => {
  const { trackSimilarSeasonsViewed, trackSimilarSeasonClicked } = useAnalytics();
  const isNba = league === "nba";
  const isMlb = league === "mlb";
  const isNfl = league === "nfl";
  const nbaSkip = !isNba || !playerId || !season;
  const mlbSkip = !isMlb || !playerId || !season || !role;
  const nflSkip = !isNfl || !playerId || !season || !role;
  const model = resolveDevSimilarityModel();
  const nbaQuery = useGetNbaSimilarSeasonsQuery(
    model ? { playerId, season, model } : { playerId, season },
    { skip: nbaSkip }
  );
  const mlbQuery = useGetMlbSimilarSeasonsQuery(
    { playerId, season, role },
    { skip: mlbSkip }
  );
  const nflQuery = useGetNflSimilarSeasonsQuery(
    { playerId, season, role },
    { skip: nflSkip }
  );
  const activeQuery = isMlb ? mlbQuery : isNfl ? nflQuery : nbaQuery;
  const display = isMlb
    ? MLB_SIMILAR_SEASON_DISPLAY
    : isNfl
    ? NFL_SIMILAR_SEASON_DISPLAY
    : NBA_SIMILAR_SEASON_DISPLAY;
  const items = toSimilarSeasonItems(
    activeQuery.data?.results || [],
    league,
    display,
    isMlb || isNfl ? { role } : undefined
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.location.hash !== `#${SIMILAR_SEASONS_ANCHOR}`) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(SIMILAR_SEASONS_ANCHOR)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [playerId, season]);

  useEffect(() => {
    if (!isNba && !isMlb && !isNfl) return;
    const activeSkip = isMlb ? mlbSkip : isNfl ? nflSkip : nbaSkip;
    if (
      activeSkip ||
      activeQuery.isLoading ||
      activeQuery.error ||
      !activeQuery.data
    ) {
      return;
    }
    trackSimilarSeasonsViewed({
      league,
      playerId,
      season,
      role,
      resultCount: items.length,
    });
  }, [
    activeQuery.data,
    activeQuery.error,
    activeQuery.isLoading,
    isMlb,
    isNba,
    isNfl,
    items.length,
    league,
    mlbSkip,
    nbaSkip,
    nflSkip,
    playerId,
    role,
    season,
    trackSimilarSeasonsViewed,
  ]);

  if (!isNba && !isMlb && !isNfl) return null;

  const handleSelect = (item) => {
    trackSimilarSeasonClicked({
      league,
      role,
      sourcePlayerId: playerId,
      sourceSeason: season,
      destPlayerId: item.playerId,
      destSeason: item.season,
      resultRank: item.rank,
    });
  };

  return (
    <HomeSection
      id={SIMILAR_SEASONS_ANCHOR}
      title="Similar Seasons"
      subtitle={similarSeasonsGuideCopy({ league, role, season }).what}
      sx={{ scrollMarginTop: { xs: 80, md: 16 } }}
    >
      <SimilarSeasonsGuide league={league} role={role} />
      <SeasonChipRail
        seasons={seasons}
        value={season}
        onChange={onSeasonChange}
      />
      <SimilarSeasonsList
        items={items}
        isLoading={activeQuery.isLoading}
        error={activeQuery.error}
        ineligibleMessage={display.ineligibleMessage}
        onSelect={handleSelect}
      />
    </HomeSection>
  );
};

export default SimilarSeasons;
